import React, { useEffect, useRef } from 'react';
import {
  Plus,
  Trash2,
  Copy,
  MoveUp,
  MoveDown,
  Layout,
  Box,
  Component,
  Sparkles,
  Search,
  Heart,
  ShoppingBag,
  User,
  Menu,
  ChevronDown,
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  ShieldCheck,
  ExternalLink,
} from 'lucide-react';
import { WIDGET_DEFINITIONS } from './widgets/widgetRegistry';
import { DEFAULT_HEADER_CONFIG, DEFAULT_FOOTER_CONFIG } from './schema/pageDocumentModel';

export default function BuilderCanvas({
  document = {},
  selectedNodeId = null,
  onSelectNode,
  onMoveNode,
  onDuplicateNode,
  onDeleteNode,
  onAddSection,
  onAddContainer,
  onAddWidgetToContainer,
  viewport = 'desktop', // desktop, tablet, mobile
  isPreviewMode = false,
}) {
  const canvasRef = useRef(null);
  const sections = document.sections || [];

  // Intercept all <a> link clicks inside canvas so the user is not navigated away while editing
  useEffect(() => {
    const handleLinkClick = (e) => {
      const target = e.target.closest('a');
      if (target) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const el = canvasRef.current;
    if (el) {
      el.addEventListener('click', handleLinkClick, true);
    }
    return () => {
      if (el) el.removeEventListener('click', handleLinkClick, true);
    };
  }, []);

  // Viewport width styling
  let containerWidthClass = 'w-full';
  if (viewport === 'tablet') containerWidthClass = 'max-w-[768px] shadow-2xl my-6 rounded-2xl border border-slate-300';
  if (viewport === 'mobile') containerWidthClass = 'max-w-[375px] shadow-2xl my-6 rounded-3xl border-2 border-slate-800';

  return (
    <div
      ref={canvasRef}
      onClick={() => onSelectNode && onSelectNode(null)}
      className="flex-1 overflow-y-auto bg-slate-100 flex flex-col items-center min-h-0 relative transition-all duration-300"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`bg-white min-h-full transition-all duration-300 flex flex-col ${containerWidthClass}`}
        style={{
          boxShadow: isPreviewMode && viewport === 'desktop' ? 'none' : undefined,
        }}
      >
        {/* ============================================================ */}
        {/* LIVE WEBSITE HEADER (Interactive & Editable)                 */}
        {/* ============================================================ */}
        {(() => {
          const header = document.header || DEFAULT_HEADER_CONFIG;
          const isHeaderSelected = !isPreviewMode && selectedNodeId === 'website-header';
          const navItems = (header.nav_items || DEFAULT_HEADER_CONFIG.nav_items).filter(
            (item) => item.enabled !== false
          );

          return (
            <header
              data-testid="live-website-header"
              onClick={(e) => {
                e.stopPropagation();
                if (!isPreviewMode) onSelectNode('website-header');
              }}
              style={{
                backgroundColor: header.background_color || '#2B3380',
                color: header.text_color || '#FFFFFF',
                paddingTop: header.padding_y || '14px',
                paddingBottom: header.padding_y || '14px',
                paddingLeft: header.padding_x || '24px',
                paddingRight: header.padding_x || '24px',
              }}
              className={`relative transition-all select-none ${
                !isPreviewMode
                  ? isHeaderSelected
                    ? 'outline-2 outline-indigo-400 outline-offset-[-2px] z-30'
                    : 'hover:outline-1 hover:outline-indigo-300 hover:outline-dashed outline-offset-[-1px] cursor-pointer'
                  : ''
              }`}
            >
              {/* Floating Header Toolbar when Selected */}
              {isHeaderSelected && (
                <div className="absolute -top-7 left-4 bg-[#2b3380] text-white rounded-t-lg px-2.5 py-1 text-[11px] font-bold flex items-center gap-2 shadow-md z-40 border border-indigo-400">
                  <span className="flex items-center gap-1">
                    <Layout className="w-3 h-3 text-indigo-300" />
                    <span>Website Header & Navigation</span>
                  </span>
                  <span className="text-[10px] text-indigo-200 font-normal">
                    (Click to edit in Inspector)
                  </span>
                </div>
              )}

              <div className="max-w-[1240px] mx-auto flex items-center justify-between gap-4">
                {/* Brand Logo */}
                <div className="flex items-center gap-3 flex-shrink-0">
                  {header.logo_url ? (
                    <img
                      src={header.logo_url}
                      alt={header.logo_alt || 'ZOBOX'}
                      className="h-8 md:h-9 w-auto object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : null}
                  <span className="font-extrabold text-lg md:text-xl tracking-tight text-white">
                    {header.website_name || 'ZOBOX'}
                  </span>
                </div>

                {/* Desktop / Tablet Navigation Links */}
                {viewport !== 'mobile' && (
                  <nav className="hidden sm:flex items-center gap-1 md:gap-2 flex-wrap">
                    {navItems.map((item) => {
                      const isActive =
                        document.route && item.url && document.route === item.url;
                      return (
                        <span
                          key={item.id || item.title}
                          className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                            isActive
                              ? 'bg-white/20 text-white font-bold'
                              : 'text-white/90 hover:text-white hover:bg-white/10'
                          }`}
                        >
                          {item.title}
                        </span>
                      );
                    })}
                  </nav>
                )}

                {/* Actions & Utilities */}
                <div className="flex items-center gap-3">
                  {/* Search */}
                  {header.show_search !== false && viewport !== 'mobile' && (
                    <div className="hidden lg:flex items-center bg-white/10 hover:bg-white/20 rounded-full px-3 py-1 text-xs text-white/80 border border-white/20 transition-colors">
                      <Search className="w-3.5 h-3.5 mr-2 opacity-70" />
                      <span className="text-white/60">
                        {header.search_placeholder || 'Search devices...'}
                      </span>
                    </div>
                  )}

                  {/* Support CTA Button */}
                  {header.show_support !== false && header.support_label && (
                    <span className="hidden md:inline-flex px-3 py-1 rounded-full text-xs font-bold bg-white text-[#2B3380] hover:bg-white/90 transition-all shadow-xs cursor-pointer">
                      {header.support_label}
                    </span>
                  )}

                  {/* Icon Actions */}
                  {header.show_actions !== false && (
                    <div className="flex items-center gap-2 text-white">
                      <div className="p-1.5 rounded-lg hover:bg-white/10 cursor-pointer relative" title="Search">
                        <Search className="w-4 h-4" />
                      </div>
                      <div className="p-1.5 rounded-lg hover:bg-white/10 cursor-pointer relative" title="Wishlist">
                        <Heart className="w-4 h-4" />
                        <span className="absolute -top-0.5 -right-0.5 bg-rose-500 text-white text-[9px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center">
                          0
                        </span>
                      </div>
                      <div className="p-1.5 rounded-lg hover:bg-white/10 cursor-pointer relative" title="Cart">
                        <ShoppingBag className="w-4 h-4" />
                        <span className="absolute -top-0.5 -right-0.5 bg-amber-400 text-[#2B3380] text-[9px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center">
                          1
                        </span>
                      </div>
                      <div className="p-1.5 rounded-lg hover:bg-white/10 cursor-pointer" title="Account">
                        <User className="w-4 h-4" />
                      </div>
                    </div>
                  )}

                  {/* Mobile Menu Icon */}
                  {viewport === 'mobile' && (
                    <div className="p-1.5 rounded-lg hover:bg-white/10 text-white cursor-pointer">
                      <Menu className="w-5 h-5" />
                    </div>
                  )}
                </div>
              </div>
            </header>
          );
        })()}

        {/* Empty State */}
        {sections.length === 0 ? (
          <div className="py-24 px-6 text-center space-y-4">
            <div className="w-16 h-16 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center mx-auto text-[#2b3380]">
              <Sparkles className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Your page canvas is empty</h2>
              <p className="text-xs text-slate-500 max-w-sm mx-auto mt-1">
                Start by adding a new section or drag elements from the left panel.
              </p>
            </div>
            <button
              type="button"
              onClick={onAddSection}
              className="px-5 py-2.5 bg-[#2b3380] hover:bg-[#1e2669] text-white rounded-xl text-xs font-bold shadow-sm transition-all"
            >
              + Add First Section
            </button>
          </div>
        ) : (
          sections.map((sec, secIdx) => {
            const isSecSelected = !isPreviewMode && selectedNodeId === sec.id;
            const isSecVisible =
              sec.visibility?.[viewport] !== false &&
              (viewport === 'desktop' ? sec.visibility?.desktop !== false : true);

            if (!isSecVisible && isPreviewMode) return null;

            return (
              <section
                key={sec.id}
                id={sec.anchorId || undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isPreviewMode) onSelectNode(sec.id);
                }}
                style={{
                  backgroundColor: sec.style?.backgroundColor || 'transparent',
                  paddingTop: sec.responsive?.[viewport]?.paddingTop || sec.style?.paddingTop || '40px',
                  paddingBottom: sec.responsive?.[viewport]?.paddingBottom || sec.style?.paddingBottom || '40px',
                  paddingLeft: sec.style?.paddingLeft || '24px',
                  paddingRight: sec.style?.paddingRight || '24px',
                  borderTopWidth: sec.style?.borderWidth || '0px',
                  borderBottomWidth: sec.style?.borderWidth || '0px',
                  borderColor: sec.style?.borderColor || '#e2e8f0',
                  borderStyle: sec.style?.borderStyle || 'solid',
                  opacity: !isSecVisible ? 0.4 : 1,
                }}
                className={`relative transition-all ${
                  !isPreviewMode
                    ? isSecSelected
                      ? 'outline-2 outline-[#2b3380] outline-offset-[-2px] z-20'
                      : 'hover:outline-1 hover:outline-indigo-300 hover:outline-dashed outline-offset-[-1px]'
                    : ''
                }`}
              >
                {/* Floating Section Toolbar when Selected */}
                {isSecSelected && (
                  <div className="absolute -top-7 left-4 bg-[#2b3380] text-white rounded-t-lg px-2.5 py-1 text-[11px] font-bold flex items-center gap-2 shadow-md z-30">
                    <span className="flex items-center gap-1">
                      <Layout className="w-3 h-3" />
                      <span>{sec.name || 'Section'}</span>
                    </span>
                    <button type="button" onClick={(e) => { e.stopPropagation(); onAddContainer(sec.id); }} className="p-1 hover:bg-white/20 rounded" title="Add columns to section"><Plus className="w-3 h-3" /></button>
                    <div className="flex items-center gap-0.5 border-l border-white/30 pl-1.5">
                      <button
                        type="button"
                        disabled={secIdx === 0}
                        onClick={(e) => {
                          e.stopPropagation();
                          onMoveNode(sec.id, -1);
                        }}
                        className="p-1 hover:bg-white/20 rounded disabled:opacity-30"
                        title="Move Section Up"
                      >
                        <MoveUp className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        disabled={secIdx === sections.length - 1}
                        onClick={(e) => {
                          e.stopPropagation();
                          onMoveNode(sec.id, 1);
                        }}
                        className="p-1 hover:bg-white/20 rounded disabled:opacity-30"
                        title="Move Section Down"
                      >
                        <MoveDown className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDuplicateNode(sec.id);
                        }}
                        className="p-1 hover:bg-white/20 rounded"
                        title="Duplicate Section"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteNode(sec.id);
                        }}
                        className="p-1 hover:bg-red-500 rounded"
                        title="Delete Section"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                )}

                {/* CONTAINERS */}
                {Array.isArray(sec.children) &&
                  sec.children.map((con) => {
                    const isConSelected = !isPreviewMode && selectedNodeId === con.id;
                    const isConVisible =
                      con.visibility?.[viewport] !== false &&
                      (viewport === 'desktop' ? con.visibility?.desktop !== false : true);

                    if (!isConVisible && isPreviewMode) return null;

                    // Column layout classes
                    let colGridClass = 'grid grid-cols-1';
                    if (viewport !== 'mobile') {
                      if (con.layout === '2-column') colGridClass = 'grid grid-cols-1 md:grid-cols-2';
                      if (con.layout === '3-column') colGridClass = 'grid grid-cols-1 md:grid-cols-3';
                      if (con.layout === '4-column') colGridClass = 'grid grid-cols-2 md:grid-cols-4';
                      if (con.layout === 'sidebar-left') colGridClass = 'grid grid-cols-1 md:grid-cols-12';
                      if (con.layout === 'sidebar-right') colGridClass = 'grid grid-cols-1 md:grid-cols-12';
                    }

                    return (
                      <div
                        key={con.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isPreviewMode) onSelectNode(con.id);
                        }}
                        style={{
                          maxWidth: con.style?.maxWidth || '1200px',
                          margin: con.style?.margin || '0 auto',
                          gap: con.style?.gap || '24px',
                        }}
                        className={`relative transition-all ${colGridClass} ${
                          !isPreviewMode
                            ? isConSelected
                              ? 'outline-2 outline-sky-500 outline-offset-1 rounded-xl p-2 bg-sky-50/20 z-10'
                              : 'hover:outline-1 hover:outline-sky-300 hover:outline-dashed rounded-lg p-1'
                            : ''
                        }`}
                      >
                        {/* Floating Container Toolbar */}
                        {isConSelected && (
                          <div className="absolute -top-6 left-2 bg-sky-600 text-white rounded-t px-2 py-0.5 text-[10px] font-bold flex items-center gap-1.5 z-20">
                            <Box className="w-2.5 h-2.5" />
                            <span>{con.name || `Container (${con.layout})`}</span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteNode(con.id);
                              }}
                              className="hover:text-red-200 ml-1"
                            >
                              ✕
                            </button>
                          </div>
                        )}

                        {/* WIDGETS */}
                        {Array.isArray(con.children) && con.children.length > 0 ? (
                          con.children.map((wgt, wgtIdx) => {
                            const isWgtSelected = !isPreviewMode && selectedNodeId === wgt.id;
                            const isWgtVisible =
                              wgt.visibility?.[viewport] !== false &&
                              (viewport === 'desktop' ? wgt.visibility?.desktop !== false : true);

                            if (!isWgtVisible && isPreviewMode) return null;

                            const def = WIDGET_DEFINITIONS[wgt.type];
                            const WidgetRenderer = def?.render;

                            // Sidebar special column spanning
                            let spanClass = '';
                            if (con.layout === 'sidebar-left') {
                              spanClass = wgtIdx === 0 ? 'md:col-span-4' : 'md:col-span-8';
                            } else if (con.layout === 'sidebar-right') {
                              spanClass = wgtIdx === 0 ? 'md:col-span-8' : 'md:col-span-4';
                            }

                            return (
                              <div
                                key={wgt.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!isPreviewMode) onSelectNode(wgt.id);
                                }}
                                className={`relative transition-all group/wgt ${spanClass} ${
                                  !isPreviewMode
                                    ? isWgtSelected
                                      ? 'outline-2 outline-[#2b3380] outline-offset-2 rounded-lg z-20 bg-indigo-50/10'
                                      : 'hover:outline-1 hover:outline-indigo-400 hover:outline-dashed rounded'
                                    : ''
                                }`}
                              >
                                {/* Floating Widget Toolbar when Selected */}
                                {isWgtSelected && (
                                  <div className="absolute -top-6 right-1 bg-[#2b3380] text-white rounded-t px-2 py-0.5 text-[10px] font-bold flex items-center gap-1.5 z-30 shadow">
                                    <Component className="w-2.5 h-2.5" />
                                    <span>{wgt.name || wgt.type}</span>
                                    <div className="flex items-center gap-1 border-l border-white/30 pl-1">
                                      <button
                                        type="button"
                                        disabled={wgtIdx === 0}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onMoveNode(wgt.id, -1);
                                        }}
                                        className="p-0.5 hover:bg-white/20 rounded disabled:opacity-30"
                                        title="Move Widget Up"
                                      >
                                        <MoveUp className="w-2.5 h-2.5" />
                                      </button>
                                      <button
                                        type="button"
                                        disabled={wgtIdx === con.children.length - 1}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onMoveNode(wgt.id, 1);
                                        }}
                                        className="p-0.5 hover:bg-white/20 rounded disabled:opacity-30"
                                        title="Move Widget Down"
                                      >
                                        <MoveDown className="w-2.5 h-2.5" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onDuplicateNode(wgt.id);
                                        }}
                                        className="p-0.5 hover:bg-white/20 rounded"
                                        title="Duplicate Widget"
                                      >
                                        <Copy className="w-2.5 h-2.5" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onDeleteNode(wgt.id);
                                        }}
                                        className="p-0.5 hover:bg-red-500 rounded"
                                        title="Delete Widget"
                                      >
                                        <Trash2 className="w-2.5 h-2.5" />
                                      </button>
                                    </div>
                                  </div>
                                )}

                                {/* RENDER THE WIDGET */}
                                {WidgetRenderer ? (
                                  <WidgetRenderer content={wgt.content} style={wgt.style} />
                                ) : (
                                  <div className="p-4 border border-dashed rounded bg-slate-50 text-slate-400 text-xs">
                                    Unsupported Widget ({wgt.type})
                                  </div>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          /* Empty Container Drop Zone */
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              onAddWidgetToContainer && onAddWidgetToContainer(con.id);
                            }}
                            className="p-6 border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center text-slate-400 hover:border-[#2b3380] hover:text-[#2b3380] hover:bg-indigo-50/30 transition-all cursor-pointer select-none"
                          >
                            <Plus className="w-4 h-4 mb-1" />
                            <span className="text-xs font-semibold">
                              Click to Add Elements to Container
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </section>
            );
          })
        )}

        {/* Append Section Button at bottom of Canvas */}
        {!isPreviewMode && sections.length > 0 && (
          <div className="p-6 text-center">
            <button
              type="button"
              onClick={onAddSection}
              className="px-4 py-2 border-2 border-dashed border-slate-300 hover:border-[#2b3380] text-slate-500 hover:text-[#2b3380] hover:bg-indigo-50/40 rounded-xl text-xs font-bold transition-all inline-flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" />
              <span>Add New Section</span>
            </button>
          </div>
        )}

        {/* ============================================================ */}
        {/* LIVE WEBSITE FOOTER (Interactive & Editable)                 */}
        {/* ============================================================ */}
        {(() => {
          const footer = document.footer || DEFAULT_FOOTER_CONFIG;
          const isFooterSelected = !isPreviewMode && selectedNodeId === 'website-footer';
          const socialMap = {
            facebook: Facebook,
            instagram: Instagram,
            linkedin: Linkedin,
            youtube: Youtube,
          };

          return (
            <footer
              data-testid="live-website-footer"
              onClick={(e) => {
                e.stopPropagation();
                if (!isPreviewMode) onSelectNode('website-footer');
              }}
              style={{
                backgroundColor: footer.background_color || '#FFFFFF',
                color: footer.text_color || '#334155',
                paddingTop: footer.padding_top || '48px',
                paddingBottom: footer.padding_bottom || '32px',
              }}
              className={`border-t border-slate-200 relative transition-all select-none ${
                !isPreviewMode
                  ? isFooterSelected
                    ? 'outline-2 outline-[#2b3380] outline-offset-[-2px] z-30'
                    : 'hover:outline-1 hover:outline-indigo-300 hover:outline-dashed outline-offset-[-1px] cursor-pointer'
                  : ''
              }`}
            >
              {/* Floating Footer Toolbar when Selected */}
              {isFooterSelected && (
                <div className="absolute -top-7 left-4 bg-[#2b3380] text-white rounded-t-lg px-2.5 py-1 text-[11px] font-bold flex items-center gap-2 shadow-md z-40">
                  <span className="flex items-center gap-1">
                    <Layout className="w-3 h-3 text-indigo-300" />
                    <span>Website Footer</span>
                  </span>
                  <span className="text-[10px] text-indigo-200 font-normal">
                    (Click to edit in Inspector)
                  </span>
                </div>
              )}

              <div className="max-w-[1240px] mx-auto px-6 space-y-8">
                {/* Top Row: Brand, Prompt, Actions & Social */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-8 pb-8 border-b border-slate-100">
                  <div className="md:col-span-1 space-y-4">
                    {footer.brand_logo && (
                      <img
                        src={footer.brand_logo}
                        alt="ZOBOX"
                        className="h-8 w-auto object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    {footer.prompt && (
                      <p className="text-xs text-slate-600 font-medium">
                        {footer.prompt}
                      </p>
                    )}
                    {footer.show_actions !== false && footer.actions?.length > 0 && (
                      <div className="flex flex-wrap gap-2 pt-1">
                        {footer.actions.map((act) => (
                          <span
                            key={act.id || act.label}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-all ${
                              act.variant === 'secondary'
                                ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                : 'bg-[#2B3380] text-white hover:bg-[#1f2563]'
                            }`}
                          >
                            {act.label}
                          </span>
                        ))}
                      </div>
                    )}
                    {footer.show_social !== false && footer.social_links?.length > 0 && (
                      <div className="pt-2">
                        {footer.social_label && (
                          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block mb-2">
                            {footer.social_label}
                          </span>
                        )}
                        <div className="flex items-center gap-2">
                          {footer.social_links.map((soc) => {
                            const IconComponent = socialMap[soc.platform] || Facebook;
                            return (
                              <span
                                key={soc.id || soc.platform}
                                className="w-7 h-7 rounded-full bg-slate-100 hover:bg-indigo-50 hover:text-[#2B3380] flex items-center justify-center text-slate-500 cursor-pointer transition-colors"
                                title={soc.label}
                              >
                                <IconComponent className="w-3.5 h-3.5" />
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Columns */}
                  <div className="md:col-span-3 grid grid-cols-2 sm:grid-cols-3 gap-6">
                    {(footer.columns || DEFAULT_FOOTER_CONFIG.columns).map((col) => (
                      <div key={col.id || col.title} className="space-y-3">
                        <strong className="text-xs font-bold uppercase tracking-wider text-slate-800 block">
                          {col.title}
                        </strong>
                        <ul className="space-y-1.5">
                          {(col.links || []).map((link) => (
                            <li key={link.id || link.label}>
                              <span className="text-xs text-slate-500 hover:text-[#2B3380] cursor-pointer transition-colors">
                                {link.label}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Office Locations & Disclaimer */}
                {footer.show_offices !== false && footer.office_locations?.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-slate-500 py-2">
                    {footer.office_locations.map((loc) => (
                      <p key={loc.id || loc.label} className="leading-relaxed">
                        <strong className="text-slate-700">{loc.label}: </strong>
                        <span>{loc.address}</span>
                      </p>
                    ))}
                  </div>
                )}

                {footer.show_disclaimer !== false && footer.disclaimer && (
                  <div className="bg-amber-50/60 border border-amber-200/60 rounded-xl p-3 flex items-start gap-2.5 text-[11px] text-amber-900 leading-relaxed">
                    <ShieldCheck className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span>{footer.disclaimer}</span>
                  </div>
                )}

                {/* Bottom Bar: Copyright & Legal */}
                <div className="pt-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-slate-400">
                  <span>
                    {(footer.copyright || '© 2025 Zobox Protech Limited. All rights reserved.').replace(
                      '{year}',
                      new Date().getFullYear()
                    )}
                  </span>
                  <div className="flex items-center gap-4 flex-wrap">
                    <span className="hover:text-slate-700 cursor-pointer">Privacy Policy</span>
                    <span className="hover:text-slate-700 cursor-pointer">Warranty Policy</span>
                    <span className="hover:text-slate-700 cursor-pointer">Terms & Conditions</span>
                    <span className="hover:text-slate-700 cursor-pointer">Return & Refund Policy</span>
                  </div>
                </div>
              </div>
            </footer>
          );
        })()}
      </div>
    </div>
  );
}
